/**
 * «Финансы → P&L → Зарплата»: из чего сложилась строка «ФОТ: зарплата» —
 * наличные кассы CRM и операции банка — и кому выплачено: по салонам,
 * сотрудникам, ролям. Безнал касс CRM — справочно: в P&L его деньги пришли
 * выпиской, а разбивка по людям есть только в CRM. Клик по салону — его
 * сотрудники (`location_id`); банк на салоны не делится — сводка скрыта.
 * По сотрудникам — принятые часы CRM; клик по имени — панель с историей
 * сотрудника (`person`); роли отбираются галочками (`role`).
 *
 * Спеки: `docs/specs/2026-09-30-payroll-breakdown-design.md`,
 * `docs/specs/2026-10-02-payroll-hours-person-design.md`.
 */
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { EntrepreneurPicker, entrepreneurIds, withEntrepreneurs } from "@/components/EntrepreneurPicker";
import { NoAccess } from "@/components/NoAccess";
import { PNL, useCan } from "@/lib/access";
import {
  loadArticles,
  loadPayroll,
  money,
  monthLabel,
  type Payroll,
  type PayrollGroup,
  type PayrollRow,
  type PnlLine,
} from "@/lib/pnl";
import { type DirectoryRow, ENTREPRENEURS, loadDirectory } from "@/lib/statements";

const CELL = "whitespace-nowrap border-b border-border px-2.5 py-1 text-right tabular-nums";
const STICKY = "sticky left-0 z-10 min-w-[260px] border-b border-r border-border px-3 py-1 text-left";
const HOURS = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const ROLE_KEY = "role";

const GROUPS: [PayrollGroup, string][] = [
  ["location", "по салонам"],
  ["user", "по сотрудникам"],
  ["role", "по ролям"],
];

type Mode = "cash" | "noncash" | "all";
const MODES: [Mode, string][] = [
  ["cash", "наличные"],
  ["noncash", "безнал по CRM"],
  ["all", "всего"],
];

export function PayrollPage() {
  return useCan(PNL) ? <PayrollScreen /> : <NoAccess what="зарплате" />;
}

function PayrollScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [entrepreneurs, setEntrepreneurs] = useState<DirectoryRow[]>([]);
  const [payrollArticle, setPayrollArticle] = useState<number | undefined>();
  const [report, setReport] = useState<Payroll | null>(null);
  const [failure, setFailure] = useState("");

  const filter = useMemo(
    () => ({
      group: (params.get("group") as PayrollGroup | null) ?? (params.get("location_id") ? "user" : "location"),
      entrepreneur_id: entrepreneurIds(params),
      business: params.get("business") !== "false",
      personal: params.get("personal") !== "false",
      cash: params.get("cash") !== "false",
      location_id: Number(params.get("location_id")) || undefined,
      date_from: params.get("date_from") ?? undefined,
      date_to: params.get("date_to") ?? undefined,
    }),
    [params],
  );
  const mode = (params.get("mode") as Mode | null) ?? "all";
  const roles = params.getAll(ROLE_KEY);
  const person = Number(params.get("person")) || undefined;

  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const setPeriod = (from: string, to: string) => setParams(withPeriod(params, from, to));

  const setRoles = (chosen: string[]) => {
    const next = new URLSearchParams(params);
    next.delete(ROLE_KEY);
    for (const role of chosen) next.append(ROLE_KEY, role);
    setParams(next);
  };

  /** Все выплаты сотрудника на экране «Кассы» — без фильтров экрана, как его история. */
  const kassaUrl = (crmId: number, label: string | null) => {
    const next = new URLSearchParams();
    if (payrollArticle) next.set("article_id", String(payrollArticle));
    next.set("user_crm_id", String(crmId));
    if (label) next.set("label", label);
    return `/kassa?${next}`;
  };

  useEffect(() => {
    loadDirectory(ENTREPRENEURS, true)
      .then(setEntrepreneurs)
      .catch((error: Error) => setFailure(error.message));
    loadArticles()
      .then((all) => setPayrollArticle(all.find((a) => a.code === "payroll")?.id))
      .catch((error: Error) => setFailure(error.message));
  }, []);

  useEffect(() => {
    setReport(null);
    setFailure("");
    loadPayroll(filter)
      .then(setReport)
      .catch((error: Error) => setFailure(error.message));
  }, [filter]);

  /** Салон — провал в его сотрудников; остальное — выплаты на экране «Кассы». */
  const openRow = (row: PayrollRow) => {
    if (row.key === null) return;
    if (filter.group === "location") {
      const next = new URLSearchParams(params);
      next.delete("group");
      next.set("location_id", String(row.key));
      if (row.name) next.set("location", row.name);
      setParams(next);
      return;
    }
    const next = withEntrepreneurs(new URLSearchParams(), filter.entrepreneur_id);
    if (payrollArticle) next.set("article_id", String(payrollArticle));
    if (mode !== "all") next.set("kind", mode === "cash" ? "cash" : "document");
    const key = { location: "location_id", user: "user_crm_id", role: "role" }[filter.group];
    next.set(key, String(row.key));
    if (filter.location_id) next.set("location_id", String(filter.location_id));
    const salon = params.get("location");
    if (row.name) next.set("label", salon ? `${row.name} · ${salon}` : row.name);
    if (filter.date_from) next.set("date_from", filter.date_from);
    if (filter.date_to) next.set("date_to", filter.date_to);
    navigate(`/kassa?${next}`);
  };

  /** ИП, тип денег, период — общие у «Зарплаты», P&L и списка операций. */
  const shared = () => {
    const next = withEntrepreneurs(new URLSearchParams(), filter.entrepreneur_id);
    if (!filter.business) next.set("business", "false");
    if (!filter.personal) next.set("personal", "false");
    if (filter.date_from) next.set("date_from", filter.date_from);
    if (filter.date_to) next.set("date_to", filter.date_to);
    return next;
  };
  const pnlBack = (() => {
    const next = shared();
    if (!filter.cash) next.set("cash", "false");
    return `/pnl?${next}`;
  })();
  // Операции банка статьи — по ним видно, кому платили.
  const bankUrl = (() => {
    if (!payrollArticle) return undefined;
    const next = shared();
    next.set("article_id", String(payrollArticle));
    return `/pnl/operations?${next}`;
  })();
  // Тип денег пришёл из P&L — видно, что выборка сужена, и снимается.
  const narrowed = [filter.business ? "" : "без счетов ИП", filter.cash ? "" : "без наличных"].filter(Boolean).join(", ");
  const widen = () => {
    const next = new URLSearchParams(params);
    for (const key of ["business", "cash"]) next.delete(key);
    setParams(next);
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <Link to={pnlBack} className="text-accent-text hover:underline">
          ← P&L
        </Link>
        <span className="font-medium">ФОТ: зарплата</span>
        <EntrepreneurPicker
          entrepreneurs={entrepreneurs}
          value={filter.entrepreneur_id}
          onChange={(ids) => setParams(withEntrepreneurs(params, ids))}
        />
        <label className="flex items-center gap-1.5 text-fg-muted" title="Для банковской части: выписки личных счетов">
          <input
            type="checkbox"
            checked={filter.personal}
            onChange={(e) => setParam("personal", e.target.checked ? undefined : "false")}
          />
          с личными счетами
        </label>
        <DateRangePicker label="период" from={filter.date_from ?? ""} to={filter.date_to ?? ""} onChange={setPeriod} />
        {narrowed ? (
          <>
            <span className="rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-1">как в P&L: {narrowed}</span>
            <button type="button" onClick={widen} className="text-accent-text hover:underline">
              снять
            </button>
          </>
        ) : null}
      </div>

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {report === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : report.months.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">Выплат зарплаты за период нет.</div>
      ) : (
        <>
          {filter.location_id ? null : <Summary report={report} bankUrl={bankUrl} />}
          <div className="flex flex-wrap items-center gap-1 text-[12.5px]">
            {filter.location_id ? (
              <span className="mr-2 inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] bg-bg-hover px-3">
                салон: <span className="font-medium">{params.get("location") ?? `№${filter.location_id}`}</span>
                <button
                  type="button"
                  title="Все салоны"
                  className="text-fg-muted hover:text-fg"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    for (const key of ["location_id", "location", "group"]) next.delete(key);
                    setParams(next);
                  }}
                >
                  ×
                </button>
              </span>
            ) : null}
            {GROUPS.filter(([group]) => !(filter.location_id && group === "location")).map(([group, label]) => (
              <Tab
                key={group}
                active={filter.group === group}
                onClick={() => setParam("group", group === (filter.location_id ? "user" : "location") ? undefined : group)}
              >
                {label}
              </Tab>
            ))}
            <span className="mx-2 h-4 w-px bg-border" />
            {MODES.map(([m, label]) => (
              <Tab key={m} active={mode === m} onClick={() => setParam("mode", m === "all" ? undefined : m)}>
                {label}
              </Tab>
            ))}
            {report.group === "location" ? null : (
              <>
                <span className="mx-2 h-4 w-px bg-border" />
                <RolePicker roles={rolesOf(report)} value={roles} onChange={setRoles} />
              </>
            )}
          </div>
          <Breakdown
            report={report}
            mode={mode}
            roles={report.group === "location" ? [] : roles}
            onOpen={openRow}
            onPerson={(row) => setParam("person", String(row.key))}
          />
        </>
      )}
      {person ? (
        <PersonPanel crmId={person} kassaUrl={kassaUrl} onClose={() => setParam("person", undefined)} />
      ) : null}
    </main>
  );
}

function Tab(props: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`h-8 rounded-[var(--radius-ui)] px-3 ${
        props.active ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:bg-bg-hover hover:text-fg"
      }`}
    >
      {props.children}
    </button>
  );
}

/** `hours` — часы под суммой (разрез по сотрудникам); `null` — время не ведётся. */
function Cells(props: { line: PnlLine; months: string[]; hours?: PnlLine | null | undefined }) {
  const { hours } = props;
  return (
    <>
      {props.months.map((m) => (
        <td key={m} className={CELL}>
          {money(props.line.values[m], true)}
          {hours === undefined ? null : <Hours value={hours?.values[m]} />}
        </td>
      ))}
      <td className={`${CELL} font-medium`}>
        {money(props.line.total, true)}
        {hours === undefined ? null : <Hours value={hours?.total} />}
      </td>
    </>
  );
}

function hoursLabel(value: string | undefined): string {
  const n = Number(value ?? 0);
  return n ? `${HOURS.format(n)} ч` : "—";
}

function Hours({ value }: { value: string | undefined }) {
  return <div className="text-[11px] font-normal text-fg-faint">{hoursLabel(value)}</div>;
}

function sumLines(lines: PnlLine[], months: string[]): PnlLine {
  const values: Record<string, string> = {};
  for (const m of months) values[m] = String(lines.reduce((acc, l) => acc + Number(l.values[m] ?? 0), 0));
  return { values, total: String(lines.reduce((acc, l) => acc + Number(l.total), 0)) };
}

/** Роль строки: у сотрудника — текущая в CRM, в разрезе «по ролям» — ключ. */
function roleOf(row: PayrollRow, group: PayrollGroup): string | null {
  return group === "role" ? (row.key as string | null) : row.role;
}

function rolesOf(report: Payroll): string[] {
  const all = new Set<string>();
  for (const row of report.rows) {
    const role = roleOf(row, report.group);
    if (role) all.add(role);
  }
  return [...all].sort((a, b) => a.localeCompare(b, "ru"));
}

/** Выбор ролей галочками — как выбор ИП; пусто — все роли. */
function RolePicker(props: { roles: string[]; value: string[]; onChange: (roles: string[]) => void }) {
  const { roles, value } = props;
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const label = value.length === 0 ? "Все роли" : value.length === 1 ? value[0] : `Роли: ${value.length}`;
  const toggle = (role: string) =>
    props.onChange(value.includes(role) ? value.filter((v) => v !== role) : [...value, role]);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={value.join(", ") || "Все роли"}
        className={`flex h-8 max-w-[260px] items-center gap-1.5 rounded-[var(--radius-ui)] border bg-bg px-2 outline-none hover:border-border-strong ${
          value.length ? "border-accent" : "border-border"
        }`}
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={13} className="flex-none text-fg-faint" />
      </button>
      {open ? (
        <div className="absolute left-0 top-9 z-40 max-h-[60vh] min-w-[220px] overflow-y-auto rounded-[var(--radius-card)] border border-border bg-bg py-1 shadow-lg">
          <button
            type="button"
            className={`block w-full px-3 py-1.5 text-left hover:bg-bg-hover ${value.length ? "" : "font-medium"}`}
            onClick={() => props.onChange([])}
          >
            Все роли
          </button>
          {roles.map((role) => (
            <label key={role} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-bg-hover">
              <input type="checkbox" checked={value.includes(role)} onChange={() => toggle(role)} />
              <span className="whitespace-nowrap">{role}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Head(props: { months: string[]; first: string }) {
  return (
    <thead>
      <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
        <th className={`${STICKY} bg-bg-subtle py-2`}>{props.first}</th>
        {props.months.map((m) => (
          <th key={m} className={`${CELL} py-2 font-medium`}>
            {monthLabel(m)}
          </th>
        ))}
        <th className={`${CELL} py-2 font-medium`}>итого</th>
      </tr>
    </thead>
  );
}

function Summary({ report, bankUrl }: { report: Payroll; bankUrl: string | undefined }) {
  const { months, summary } = report;
  const gap: PnlLine = diff(summary.bank, summary.crm_noncash, months);
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12.5px]">
        <Head months={months} first="откуда (выплачено, ₽)" />
        <tbody>
          <tr>
            <td className={`${STICKY} bg-bg`} title="Выплаты наличных касс CRM статьи «ФОТ: зарплата»">
              Наличные — кассы CRM <span className="text-fg-faint">· {summary.cash.count} выплат</span>
            </td>
            <Cells line={summary.cash} months={months} />
          </tr>
          <tr>
            <td className={`${STICKY} bg-bg`} title="Операции выписок статьи «ФОТ: зарплата»: реестры, переводы">
              Безнал — выписки банка{" "}
              {bankUrl && summary.bank.count ? (
                <Link to={bankUrl} className="text-accent-text hover:underline" title="Операции банка: кому платили">
                  · {summary.bank.count} операций
                </Link>
              ) : (
                <span className="text-fg-faint">· {summary.bank.count} операций</span>
              )}
            </td>
            <Cells line={summary.bank} months={months} />
          </tr>
          <tr className="bg-bg-subtle font-semibold">
            <td className={`${STICKY} bg-bg-subtle`}>Итого в P&L</td>
            <Cells line={summary.pnl} months={months} />
          </tr>
          <tr>
            <td
              colSpan={months.length + 2}
              className="border-b border-border px-3 pb-1 pt-4 text-[10.5px] font-medium uppercase tracking-[0.06em] text-fg-faint"
            >
              Справочно — в P&L не входит: деньги ушли операцией банка
            </td>
          </tr>
          <tr className="text-fg-muted">
            <td className={`${STICKY} bg-bg`}>Безнал по кассам CRM</td>
            <Cells line={summary.crm_noncash} months={months} />
          </tr>
          {summary.crm_noncash_by_kassa.map((k) => (
            <tr key={k.kassa_name} className="text-fg-muted">
              <td className={`${STICKY} bg-bg pl-6`}>{k.kassa_name}</td>
              <Cells line={k} months={months} />
            </tr>
          ))}
          <tr className="text-fg-muted">
            <td
              className={`${STICKY} bg-bg`}
              title="Выписки минус безнал касс CRM. Минус — выплаты по CRM есть, а в загруженных выписках их нет: выписка не загружена или платёж в пути"
            >
              Разница: выписки − CRM
            </td>
            {months.map((m) => {
              const v = Number(gap.values[m] ?? 0);
              return (
                <td key={m} className={`${CELL} ${v < -1 ? "text-warn" : ""}`}>
                  {money(v, true)}
                </td>
              );
            })}
            <td className={`${CELL} font-medium`}>{money(gap.total, true)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function diff(a: PnlLine, b: PnlLine, months: string[]): PnlLine {
  const values: Record<string, string> = {};
  for (const m of months) values[m] = String(Number(a.values[m] ?? 0) - Number(b.values[m] ?? 0));
  return { values, total: String(Number(a.total) - Number(b.total)) };
}

function pick(row: PayrollRow, mode: Mode, months: string[]): PnlLine {
  if (mode === "cash") return row.cash;
  if (mode === "noncash") return row.noncash;
  const values: Record<string, string> = {};
  for (const m of months) values[m] = String(Number(row.cash.values[m] ?? 0) + Number(row.noncash.values[m] ?? 0));
  return { values, total: String(Number(row.cash.total) + Number(row.noncash.total)) };
}

function Breakdown(props: {
  report: Payroll;
  mode: Mode;
  roles: string[];
  onOpen: (row: PayrollRow) => void;
  onPerson: (row: PayrollRow) => void;
}) {
  const { report, mode, roles } = props;
  const byUser = report.group === "user";
  const rows = report.rows
    .filter((row) => roles.length === 0 || roles.includes(roleOf(row, report.group) ?? ""))
    .map((row) => ({ row, line: pick(row, mode, report.months) }))
    .filter(({ line }) => Number(line.total) !== 0);
  const total = sumLines(
    rows.map(({ line }) => line),
    report.months,
  );
  const totalHours = byUser
    ? sumLines(
        rows.flatMap(({ row }) => (row.hours ? [row.hours] : [])),
        report.months,
      )
    : undefined;
  const empty = { location: "без салона", user: "без сотрудника", role: "роль не указана" }[report.group];
  const first = { location: "салон", user: "сотрудник · роль сейчас", role: "роль сейчас" }[report.group];
  return (
    <>
      <div className="text-[12px] text-fg-muted">
        Кассы CRM{mode === "all" ? " — наличные и безнал" : mode === "cash" ? " — наличные (в P&L)" : " — безнал (справочно)"}. Салон — город
        выплаты; роль — текущая в CRM (истории ролей CRM не хранит). Банковские реестры по людям не делятся — их
        разбивка здесь по безналу касс CRM.
        {byUser
          ? " Под суммой — принятые в CRM часы за месяц смены (зарплату за месяц платят и в следующем). Клик по имени — история сотрудника."
          : ""}
      </div>
      {byUser && report.hours_failed ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-warn/30 px-3 py-2 text-[12.5px] text-warn">
          CRM не ответила — часов нет, суммы верные. Обновите страницу позже.
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
        <table className="w-full border-collapse text-[12.5px]">
          <Head months={report.months} first={first} />
          <tbody>
            <tr className="bg-bg-subtle font-semibold">
              <td className={`${STICKY} bg-bg-subtle`}>
                итого{roles.length ? ` · ${roles.join(", ")}` : ""}{" "}
                <span className="font-normal text-fg-faint">· {rows.length}</span>
              </td>
              <Cells line={total} months={report.months} hours={totalHours} />
            </tr>
            {rows.map(({ row, line }) => (
              <tr
                key={String(row.key)}
                className={row.key === null ? "text-fg-muted" : "cursor-pointer hover:bg-bg-hover"}
                title={
                  row.key === null
                    ? undefined
                    : `${row.payouts} выплат — ${report.group === "location" ? "сотрудники салона" : "открыть"}`
                }
                onClick={() => props.onOpen(row)}
              >
                <td className={`${STICKY} bg-bg`}>
                  {byUser && row.key !== null ? (
                    <button
                      type="button"
                      className="text-left hover:text-accent-text hover:underline"
                      title="История сотрудника по месяцам"
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onPerson(row);
                      }}
                    >
                      {row.name ?? empty}
                    </button>
                  ) : (
                    (row.name ?? empty)
                  )}
                  {row.role ? <span className="text-fg-faint"> · {row.role}</span> : null}
                </td>
                <Cells line={line} months={report.months} hours={byUser ? row.hours : undefined} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * История сотрудника: вся его зарплата по месяцам — все ИП, салоны, время.
 * Та же ручка с `user_crm_id`; банк по людям не делится — только кассы CRM.
 */
function PersonPanel(props: {
  crmId: number;
  kassaUrl: (crmId: number, label: string | null) => string;
  onClose: () => void;
}) {
  const { crmId, onClose } = props;
  const [history, setHistory] = useState<Payroll | null>(null);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    setHistory(null);
    setFailure("");
    loadPayroll({ group: "user", user_crm_id: crmId })
      .then(setHistory)
      .catch((error: Error) => setFailure(error.message));
  }, [crmId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const row = history?.rows[0];
  // Свежий месяц сверху; полоса в «итого» — доля от лучшего месяца.
  const months = [...(history?.months ?? [])].reverse();
  const totals = months.map((m) => Number(row?.cash.values[m] ?? 0) + Number(row?.noncash.values[m] ?? 0));
  const peak = Math.max(1, ...totals);
  // Выравнивание — у каждой ячейки своё: два text-* в одном классе решает порядок CSS, не наш.
  const TH = "border-b border-border px-2 py-1.5 font-medium";
  const TD = "border-b border-border px-2 py-1";
  const NUM_TH = `${TH} text-right`;
  const NUM = `${TD} text-right`;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-[min(540px,100vw)] flex-col border-l border-border bg-bg shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <div className="text-[14px] font-medium">{row?.name ?? `Сотрудник №${crmId}`}</div>
            <div className="text-[12px] text-fg-muted">
              {row?.role ? `${row.role} · ` : ""}вся история: все ИП и салоны, кассы CRM
            </div>
          </div>
          <button
            type="button"
            title="Закрыть (Esc)"
            className="text-[18px] leading-none text-fg-muted hover:text-fg"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 text-[12.5px]">
          {failure ? (
            <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-err">
              {failure}
            </div>
          ) : history === null ? (
            <div className="text-fg-muted">…</div>
          ) : !row ? (
            <div className="text-fg-muted">Выплат зарплаты нет.</div>
          ) : (
            <>
              {history.hours_failed ? (
                <div className="mb-2 text-warn">CRM не ответила — часов нет, суммы верные.</div>
              ) : null}
              <table className="w-full border-collapse tabular-nums">
                <thead>
                  <tr className="text-[11px] text-fg-muted">
                    <th className={`${TH} pl-0 text-left`}>месяц</th>
                    <th className={NUM_TH}>наличные</th>
                    <th className={NUM_TH}>безнал CRM</th>
                    <th className={NUM_TH}>итого</th>
                    <th className={`${NUM_TH} pr-0`} title="Принятые в CRM часы за месяц смены">
                      часы
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m, i) => (
                    <tr key={m}>
                      <td className={`${TD} whitespace-nowrap pl-0 text-left`}>{monthLabel(m)}</td>
                      <td className={NUM}>{money(row.cash.values[m], true)}</td>
                      <td className={NUM}>{money(row.noncash.values[m], true)}</td>
                      <td className={`${NUM} relative font-medium`}>
                        <span
                          className="absolute inset-y-1 right-0 rounded-[2px] bg-accent-dim"
                          style={{ width: `${((totals[i] ?? 0) / peak) * 100}%` }}
                        />
                        <span className="relative">{money(totals[i], true)}</span>
                      </td>
                      <td className={`${NUM} pr-0 text-fg-muted`}>{hoursLabel(row.hours?.values[m])}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="py-1.5 text-left">итого</td>
                    <td className="px-2 py-1.5 text-right">{money(row.cash.total, true)}</td>
                    <td className="px-2 py-1.5 text-right">{money(row.noncash.total, true)}</td>
                    <td className="px-2 py-1.5 text-right">
                      {money(Number(row.cash.total) + Number(row.noncash.total), true)}
                    </td>
                    <td className="py-1.5 text-right text-fg-muted">{hoursLabel(row.hours?.total)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-3 text-[12px] text-fg-muted">
                Выплата — по дате выплаты, часы — по месяцу смены. Роль — текущая в CRM.{" "}
                <Link to={props.kassaUrl(crmId, row.name)} className="text-accent-text hover:underline">
                  Все выплаты в кассах →
                </Link>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
